/**
 * Trust Indicator Component
 * Displays platform-wide approval statistics to build user confidence
 * Shows total approved plans and top jurisdictions
 */

import { useEffect, useState } from 'react';
import { apiClient } from '../../services/api';

interface JurisdictionStats {
  jurisdiction_name: string;
  approved_count: number;
}

interface PlatformStats {
  total_approved_30d: number;
  total_jurisdictions: number;
  top_jurisdictions: JurisdictionStats[];
  last_updated: string;
}

interface ApiResponse {
  success: boolean;
  data: PlatformStats;
}

/**
 * Full trust indicator with detailed statistics
 * Suitable for prominent placement on landing pages
 */
export function TrustIndicator() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      const response = await apiClient.get<ApiResponse>('/statistics/platform');
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading skeleton while fetching data
  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 animate-pulse">
        <div className="h-20 bg-green-100 rounded"></div>
      </div>
    );
  }

  // Don't show if error or no data
  if (hasError || !stats || stats.total_approved_30d === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Trusted by California Construction Professionals
          </h3>

          <p className="text-2xl font-bold text-green-600 mb-3">
            {stats.total_approved_30d.toLocaleString()} plans approved in the last 30 days
          </p>

          {stats.top_jurisdictions.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-neutral-700 mb-2">
                Top Jurisdictions:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {stats.top_jurisdictions.slice(0, 6).map((jurisdiction) => (
                  <div
                    key={jurisdiction.jurisdiction_name}
                    className="bg-white rounded-md px-3 py-2 text-sm border border-neutral-200"
                  >
                    <div className="font-medium text-neutral-900">
                      {jurisdiction.jurisdiction_name}
                    </div>
                    <div className="text-neutral-600">
                      {jurisdiction.approved_count}{' '}
                      {jurisdiction.approved_count === 1 ? 'plan' : 'plans'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-neutral-500 mt-4">
            Statistics updated every 5 minutes • Data from California jurisdictions
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact trust badge for smaller spaces
 * Shows total approved plans in a pill format
 */
export function TrustBadge() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setHasError(false);
      const response = await apiClient.get<ApiResponse>('/statistics/platform');
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      setHasError(true);
    }
  };

  // Don't show if error or no data
  if (hasError || !stats || stats.total_approved_30d === 0) {
    return null;
  }

  return (
    <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2">
      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      <span className="text-sm font-medium text-green-800">
        {stats.total_approved_30d}+ plans approved this month
      </span>
    </div>
  );
}